import { useEffect, useRef } from "react";

import {
  isCoordinatedUpdateCanarySynchronized,
  isCoordinatedUpdateEnabled,
} from "hassibot/services_v2/service-worker/coordinated-update-canary";
import {
  createBrowserHistory,
  createPath,
  History,
  LocationDescriptorObject,
  parsePath,
  Path,
} from "history";
import { isInterAppNavigation } from "startup/app-launchers";

type UserConfirmation = (message: string, callback: (ok: boolean) => void) => void;

let activeUserConfirmation: UserConfirmation | null = null;

/**
 * Maps a requested pathname to its canonical equivalent. Registered by the
 * routing layer (see useCanonicalNavigation), so this module stays unaware of
 * any specific redirect rule.
 */
type PathnameCanonicalizer = (pathname: string) => string;

let activeCanonicalizer: PathnameCanonicalizer | null = null;

/**
 * Rewrite a navigation target's pathname through the given canonicalizer,
 * preserving search/hash/state. Returns the target untouched when the pathname
 * is already canonical. Exported for unit testing — runtime callers go through
 * the push/replace wrappers below, which inject the active canonicalizer.
 */
export const canonicalizeTarget = (
  canonicalize: PathnameCanonicalizer,
  target: Path | LocationDescriptorObject
): Path | LocationDescriptorObject => {
  if (typeof target === "string") {
    const { pathname, search, hash } = parsePath(target);
    const canonicalPathname = canonicalize(pathname);
    return canonicalPathname === pathname
      ? target
      : createPath({ pathname: canonicalPathname, search, hash });
  }

  const pathname = target.pathname ?? "";
  const canonicalPathname = canonicalize(pathname);
  return canonicalPathname === pathname ? target : { ...target, pathname: canonicalPathname };
};

const toCanonicalTarget = (
  target: Path | LocationDescriptorObject
): Path | LocationDescriptorObject =>
  activeCanonicalizer === null ? target : canonicalizeTarget(activeCanonicalizer, target);

const withCanonicalization =
  (navigate: History["push"]): History["push"] =>
  (target, state) =>
    navigate(toCanonicalTarget(target), state);

const pathnameOf = (target: Path | LocationDescriptorObject): string =>
  typeof target === "string" ? (parsePath(target).pathname ?? "") : (target.pathname ?? "");

/**
 * Transitional bridge until the coordinated update flow ships: a push toward a
 * *different* app reloads the page — as it did before the shared SPA router — so
 * a pending Service Worker update is picked up on app change. Intra-app
 * navigation stays a SPA push. The decision is made on the canonical target, and
 * only `push` is wrapped: `replace` (boot/canonical redirects) must stay in-SPA
 * to avoid reloading on startup. Remove once coordinated updates are generalised.
 */
const withInterAppReload =
  (navigate: History["push"]): History["push"] =>
  (target, state) => {
    const canonicalTarget = toCanonicalTarget(target);
    // Confirmed canary users keep a full SPA experience: updates are applied
    // manually via the update banner, so we never force a reload on app change.
    // Until `/me` confirms the cached value, keep the historical reload behaviour.
    if (isCoordinatedUpdateCanarySynchronized() && isCoordinatedUpdateEnabled()) {
      navigate(canonicalTarget, state);
      return;
    }
    if (isInterAppNavigation(window.location.pathname, pathnameOf(canonicalTarget))) {
      window.location.assign(
        typeof canonicalTarget === "string" ? canonicalTarget : createPath(canonicalTarget)
      );
      return;
    }
    navigate(canonicalTarget, state);
  };

/**
 * Single shared browser history. Every app renders under the same root
 * <Router history={browserHistory}> (see startup/main.tsx), so pushing an URL
 * of another app would navigate inside the SPA — `withInterAppReload` turns a
 * cross-app push back into a full reload (transitional, see above).
 *
 * push/replace are wrapped so a navigation toward a redirect source is rewritten
 * to its canonical target *before* listeners (e.g. the PAGE_VIEW tracker's
 * history.listen) are notified — no observer ever sees a pre-redirect path. The
 * routing-specific rule is injected at runtime via useCanonicalNavigation.
 */
export const browserHistory: History = createBrowserHistory({
  getUserConfirmation: (message, callback) => {
    activeUserConfirmation
      ? activeUserConfirmation(message, callback)
      : callback(window.confirm(message));
  },
});

browserHistory.push = withInterAppReload(browserHistory.push.bind(browserHistory));
browserHistory.replace = withCanonicalization(browserHistory.replace.bind(browserHistory));

/**
 * Register the confirmation dialog used when a navigation is blocked (e.g. a
 * react-router <Prompt> guarding a form with unsaved changes). The shared
 * history is created once, so the confirmation handler is swapped at runtime:
 * apps register theirs while mounted, and window.confirm is the fallback.
 */
export const useUserConfirmation = (confirmation: UserConfirmation): void => {
  const confirmationRef = useRef(confirmation);
  confirmationRef.current = confirmation;

  useEffect(() => {
    const delegate: UserConfirmation = (message, callback) =>
      confirmationRef.current(message, callback);
    if (activeUserConfirmation !== null) {
      console.warn("useUserConfirmation: replacing an already registered confirmation handler");
    }
    activeUserConfirmation = delegate;
    return () => {
      if (activeUserConfirmation === delegate) {
        activeUserConfirmation = null;
      }
    };
  }, []);
};

/**
 * Register the canonicalizer used to rewrite navigations toward redirect
 * sources. Mirrors useUserConfirmation: the shared history is created once, so
 * the rule — which depends on runtime context such as the current collaborator —
 * is swapped at runtime, and the latest closure is always read via a ref.
 */
export const useCanonicalNavigation = (canonicalizer: PathnameCanonicalizer): void => {
  const canonicalizerRef = useRef(canonicalizer);
  canonicalizerRef.current = canonicalizer;

  useEffect(() => {
    const delegate: PathnameCanonicalizer = pathname => canonicalizerRef.current(pathname);
    if (activeCanonicalizer !== null) {
      console.warn("useCanonicalNavigation: replacing an already registered canonicalizer");
    }
    activeCanonicalizer = delegate;
    return () => {
      if (activeCanonicalizer === delegate) {
        activeCanonicalizer = null;
      }
    };
  }, []);
};
